import { type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface ModernRailItem {
  id: string;
  label: string;
  icon: ReactNode;
  href?: string;
  badge?: number;
}

/**
 * ModernLeftRail — slim icon rail (primary nav). Quiet by default, a single
 * burgundy accent marks the active item. Navigation is data-driven: items are
 * passed in and `onSelect`/`href` is wired to the app router by the caller.
 */
export function ModernLeftRail({
  brand,
  items,
  activeId,
  onSelect,
  footer,
  className,
}: {
  brand?: ReactNode;
  items: ModernRailItem[];
  activeId?: string;
  onSelect?: (id: string) => void;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <nav
      aria-label="Primary"
      className={cn(
        'flex h-full w-[var(--mui-rail-width)] flex-shrink-0 flex-col items-center border-r border-border bg-card py-3',
        className,
      )}
    >
      {brand && <div className="mb-2 flex h-10 w-10 items-center justify-center">{brand}</div>}
      <ul className="flex flex-1 flex-col items-center gap-1">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id}>
              <button
                type="button"
                title={item.label}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                onClick={() => onSelect?.(item.id)}
                className={cn(
                  'relative flex h-11 w-11 items-center justify-center rounded-[calc(var(--radius)-4px)] transition-colors',
                  active
                    ? 'bg-primary/[0.08] text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {active && (
                  <span
                    aria-hidden
                    className="absolute -left-3 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-primary"
                  />
                )}
                {item.icon}
                {typeof item.badge === 'number' && item.badge > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold text-primary-foreground">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {footer && <div className="mt-2 flex flex-col items-center gap-2">{footer}</div>}
    </nav>
  );
}
